import { Popover } from '@headlessui/react';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import React, { useState } from 'react';
import DatePickerLib from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DatePickerProps {
  id?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  className?: string;
  placeholder?: string;
  popperClassName?: string;
  disabled?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
  filterDate?: (date: Date) => boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  id,
  value,
  onChange,
  className = '',
  placeholder,
  popperClassName,
  disabled = false,
  minDate,
  maxDate,
  filterDate
}) => {
  const [pickerMode, setPickerMode] = useState<'date' | 'month' | 'year'>(
    'date'
  );

  if (disabled) {
    return (
      <div
        id={id}
        className={`w-full relative rounded-full min-w-[200px] border bg-gray-100 text-gray-500 border-gray-300 py-2 px-4 pr-12 text-left font-medium transition appearance-none ${className}`}
        aria-disabled
      >
        <span className='truncate block'>
          {value ? value.toISOString().slice(0, 10) : placeholder}
        </span>
        <span className='absolute inset-y-0 right-4 flex items-center pointer-events-none'>
          <CalendarIcon className='h-5 w-5 text-slate-400' />
        </span>
      </div>
    );
  }

  return (
    <Popover className='relative w-full'>
      {({ close }) => (
        <>
          <Popover.Button as='div' className='w-full cursor-pointer'>
            <div
              id={id}
              className={`w-full relative rounded-full min-w-[200px] border border-gray-300 bg-white py-2 px-4 pr-12 text-left font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none ${className}`}
            >
              <span className='truncate block'>
                {value ? value.toISOString().slice(0, 10) : placeholder}
              </span>
              <span className='absolute inset-y-0 right-4 flex items-center pointer-events-none'>
                <CalendarIcon className='h-5 w-5 text-slate-400' />
              </span>
            </div>
          </Popover.Button>

          <Popover.Panel className='absolute z-50 mt-0 left-0'>
            <DatePickerLib
              selected={value}
              onChange={date => {
                if (pickerMode === 'date') {
                  onChange(date);
                  close();
                } else {
                  // Update selected month/year then return to date mode without closing
                  onChange(date);
                  setPickerMode('date');
                }
              }}
              inline
              calendarClassName='!rounded-2xl !border !border-gray-200 !shadow-2xl !bg-white !mt-2 z-50 !opacity-100'
              dayClassName={_date =>
                'rounded-full transition-colors duration-200 hover:bg-blue-100'
              }
              popperClassName={popperClassName}
              minDate={minDate ?? undefined}
              maxDate={maxDate ?? undefined}
              filterDate={filterDate}
              weekDayClassName={() => ''}
              renderCustomHeader={({
                date: headerDate,
                decreaseMonth,
                increaseMonth
              }) => (
                <div className='flex items-center justify-between px-3 pt-3 min-w-[296px]'>
                  <button
                    className='p-1 text-gray-500 text-xl hover:text-slate-700 hover:bg-gray-100 rounded'
                    onClick={decreaseMonth}
                    type='button'
                  >
                    <ChevronLeft className='h-5 w-5 text-black' />
                  </button>
                  <div className='flex items-center gap-2 font-semibold'>
                    <button
                      type='button'
                      className='hover:text-blue-600'
                      onClick={() => setPickerMode('month')}
                    >
                      {headerDate.toLocaleString(undefined, { month: 'long' })}
                    </button>
                    <button
                      type='button'
                      className='hover:text-blue-600'
                      onClick={() => setPickerMode('year')}
                    >
                      {headerDate.getFullYear()}
                    </button>
                  </div>
                  <button
                    className='p-1 text-gray-500 hover:text-slate-700 hover:bg-gray-100 rounded'
                    onClick={increaseMonth}
                    type='button'
                  >
                    <ChevronRight className='h-5 w-5 text-black' />
                  </button>
                </div>
              )}
              showMonthYearPicker={pickerMode === 'month'}
              showYearPicker={pickerMode === 'year'}
            />
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
};

export default DatePicker;
